import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import PageOne from "@/pages/PageOne";
import PageTwo from "@/pages/PageTwo";
import PageThree from "@/pages/PageThree";
import PageFour from "@/pages/PageFour";
import PageFive from "@/pages/PageFive";
import PageSix from "@/pages/PageSix";
import PageSeven from "@/pages/PageSeven";
import CompletionPage from "@/pages/CompletionPage";
import MobileWarningPage from "@/pages/MobileWarningPage";
import AdminPage from "@/pages/AdminPage";
import AdminLogin from "@/pages/AdminLogin";

// Protected Route component for admin pages
const ProtectedAdminRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        setIsAuthenticated(false);
        return;
      }

      try {
        const response = await fetch(`${BACKEND_URL}/api/admin/check-auth`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setIsAuthenticated(response.ok);
      } catch (error) {
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, [BACKEND_URL]);

  if (isAuthenticated === null) {
    // Loading state
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-tca-blue rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

// Mobile detection hook
const useIsMobileWithoutDesktopMode = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      // Check if user agent indicates mobile device
      const mobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      // When desktop mode is enabled, the user agent changes to desktop
      // So we only flag as mobile if user agent shows mobile
      setIsMobile(mobileUserAgent);
    };

    checkMobile();
    
    // Re-check on resize (in case of orientation change or desktop mode toggle)
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

// Wrapper component that redirects mobile users
const MobileGuard = ({ children }) => {
  const isMobile = useIsMobileWithoutDesktopMode();
  
  useEffect(() => {
    // Check if user was previously shown the mobile warning (they "sinned")
    const wasCaughtOnMobile = sessionStorage.getItem('mobileWarningShown');
    
    if (wasCaughtOnMobile && !isMobile) {
      // User switched back to desktop mode after being on mobile - redirect to start over
      sessionStorage.removeItem('mobileWarningShown');
      window.location.replace('https://tax.tcauditors.co.za');
      return;
    }
    
    if (isMobile) {
      // Use window.location.href to force a full page redirect
      // This clears React Router history so user can't go back
      window.location.href = '/mobile-warning';
    }
  }, [isMobile]);
  
  // Show nothing while redirecting
  if (isMobile) {
    return null;
  }
  
  // Check if user was caught and is now trying to access pages
  const wasCaughtOnMobile = sessionStorage.getItem('mobileWarningShown');
  if (wasCaughtOnMobile) {
    sessionStorage.removeItem('mobileWarningShown');
    window.location.replace('https://tax.tcauditors.co.za');
    return null;
  }
  
  return children;
};

function App() {
  return (
    <div className="min-h-screen bg-background">
      <BrowserRouter>
        <Routes>
          {/* Mobile warning page - accessible without guard */}
          <Route path="/mobile-warning" element={<MobileWarningPage />} />
          
          {/* Completion page - accessible without guard (user already completed) */}
          <Route path="/complete" element={<CompletionPage />} />
          
          {/* Admin login page - accessible without authentication */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/setup" element={<AdminLogin />} />
          
          {/* Admin dashboard - protected with authentication */}
          <Route path="/admin" element={
            <ProtectedAdminRoute>
              <AdminPage />
            </ProtectedAdminRoute>
          } />
          
          {/* All other pages protected by mobile guard */}
          <Route path="/" element={<MobileGuard><PageOne /></MobileGuard>} />
          <Route path="/page-1" element={<MobileGuard><PageOne /></MobileGuard>} />
          <Route path="/page-2" element={<MobileGuard><PageTwo /></MobileGuard>} />
          <Route path="/page-3" element={<MobileGuard><PageThree /></MobileGuard>} />
          <Route path="/page-4" element={<MobileGuard><PageFour /></MobileGuard>} />
          <Route path="/page-5" element={<MobileGuard><PageFive /></MobileGuard>} />
          <Route path="/page-6" element={<MobileGuard><PageSix /></MobileGuard>} />
          <Route path="/page-7" element={<MobileGuard><PageSeven /></MobileGuard>} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
